/**
 * Infinite Scroll Component
 *
 * Custom element that handles infinite scroll loading for collection pages
 * with a back-to-top button that appears after scrolling.
 */
class InfiniteScrollLoader extends HTMLElement {
	constructor() {
		super();

		this.containerSelector = this.dataset.containerSelector;
		this.container = document.querySelector(this.containerSelector);
		this.nextUrl = this.dataset.nextUrl;
		this.currentPage = parseInt(this.dataset.currentPage, 10);
		this.totalPages = parseInt(this.dataset.totalPages, 10);

		this.loadingEl = this.querySelector('.infinite-scroll__loading');
		this.endMessageEl = this.querySelector('.infinite-scroll__end-message');
		this.backToTopBtn = this.querySelector('.infinite-scroll__back-to-top');
		this.skeletonContainer = this.querySelector('.infinite-scroll__skeleton');

		this.isLoading = false;
		this.hasMorePages = this.currentPage < this.totalPages;

		// Bind methods for proper event listener cleanup
		this.handleScroll = this.throttle(this.onScroll.bind(this), 100);
		this.handleBackToTopClick = this.onBackToTopClick.bind(this);

		this.init();
	}

	init() {
		if (!this.container) {
			console.warn('InfiniteScrollLoader: Container not found');
			return;
		}

		this.setupIntersectionObserver();
		this.setupBackToTop();
		this.updateStatus();
	}

	/**
	 * Throttle function to limit how often a function can be called
	 */
	throttle(func, limit) {
		let inThrottle;
		return (...args) => {
			if (!inThrottle) {
				func.apply(this, args);
				inThrottle = true;
				setTimeout(() => (inThrottle = false), limit);
			}
		};
	}

	setupIntersectionObserver() {
		if (!this.loadingEl) {
			console.warn('InfiniteScrollLoader: Loading element not found');
			return;
		}

		const options = {
			root: null,
			rootMargin: '200px',
			threshold: 0,
		};

		this.observer = new IntersectionObserver((entries) => {
			entries.forEach((entry) => {
				if (entry.isIntersecting && !this.isLoading && this.hasMorePages) {
					this.loadNextPage();
				}
			});
		}, options);

		this.observer.observe(this.loadingEl);
	}

	onScroll() {
		if (!this.backToTopBtn) return;

		const scrollThreshold = 800;
		if (window.scrollY > scrollThreshold) {
			this.backToTopBtn.hidden = false;
		} else {
			this.backToTopBtn.hidden = true;
		}
	}

	onBackToTopClick() {
		window.scrollTo({
			top: 0,
			behavior: 'smooth',
		});
	}

	setupBackToTop() {
		if (!this.backToTopBtn) {
			return;
		}

		window.addEventListener('scroll', this.handleScroll, { passive: true });
		this.backToTopBtn.addEventListener('click', this.handleBackToTopClick);
	}

	showSkeleton() {
		if (this.skeletonContainer) {
			this.skeletonContainer.hidden = false;
		}
	}

	hideSkeleton() {
		if (this.skeletonContainer) {
			this.skeletonContainer.hidden = true;
		}
	}

	async loadNextPage() {
		if (this.isLoading || !this.hasMorePages || !this.nextUrl || !this.container) return;

		this.isLoading = true;
		if (this.loadingEl) {
			this.loadingEl.hidden = true;
		}
		this.showSkeleton();

		try {
			const response = await fetch(this.nextUrl);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const html = await response.text();
			const parser = new DOMParser();
			const doc = parser.parseFromString(html, 'text/html');

			const newProducts = doc.querySelectorAll(`${this.containerSelector} > .grid__item`);
			const newInfiniteScroll = doc.querySelector('infinite-scroll-loader');

			if (newProducts.length > 0) {
				newProducts.forEach((product) => {
					this.container.appendChild(product.cloneNode(true));
				});

				document.dispatchEvent(
					new CustomEvent('infinite-scroll:loaded', {
						detail: { page: this.currentPage + 1 },
					})
				);
			}

			this.currentPage += 1;

			if (newInfiniteScroll) {
				const nextUrl = newInfiniteScroll.getAttribute('data-next-url');
				const newTotalPages = parseInt(newInfiniteScroll.getAttribute('data-total-pages'), 10);

				if (nextUrl && nextUrl.length > 0) {
					this.nextUrl = nextUrl;
					this.totalPages = newTotalPages;
					this.hasMorePages = true;
				} else {
					this.nextUrl = null;
					this.hasMorePages = false;
				}
			} else {
				this.nextUrl = null;
				this.hasMorePages = false;
			}

			this.updateStatus();
		} catch (error) {
			console.error('InfiniteScrollLoader: Error loading next page', error);
			if (this.loadingEl) {
				this.loadingEl.hidden = false;
			}
		} finally {
			this.isLoading = false;
			this.hideSkeleton();
		}
	}

	updateStatus() {
		if (!this.hasMorePages) {
			if (this.loadingEl) {
				this.loadingEl.hidden = true;
			}
			if (this.endMessageEl) {
				this.endMessageEl.hidden = false;
			}

			if (this.observer) {
				this.observer.disconnect();
			}
		} else {
			if (this.loadingEl) {
				this.loadingEl.hidden = false;
			}
		}
	}

	disconnectedCallback() {
		if (this.observer) {
			this.observer.disconnect();
		}

		window.removeEventListener('scroll', this.handleScroll);

		if (this.backToTopBtn) {
			this.backToTopBtn.removeEventListener('click', this.handleBackToTopClick);
		}
	}
}

customElements.define('infinite-scroll-loader', InfiniteScrollLoader);
